import * as React from "react";

import { CloseOutlined } from "@ant-design/icons";
import { AsyncComponent2 } from "@impero-com/common/components/asyncComponent";
import {
    ColumnBuilder,
    DataTable,
} from "@impero-com/common/components/dataTable";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import * as Cmp from "@impero-com/common/utils/comparison";
import {
    ArrayDataSource,
    DataExtractor,
    DataView,
} from "@impero-com/common/utils/dataView";
import { parseDate } from "@impero-com/common/utils/date";
import { entries, fromEntries } from "@impero-com/common/utils/entries";
import { formatDateTimeFast } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    Button,
    InformationModal,
    InfoTooltip,
    Modal,
    PageSpinner,
} from "@impero-com/ui";
import { Input, Popconfirm, Spin, Tree, notification } from "antd";
import type {
    TreeProps,
    TreeNodeNormal,
    AntdTreeNodeAttribute,
} from "antd/es/tree/Tree";
import memoizeOne from "memoize-one";
import moment, { Moment } from "moment-timezone";

import {
    deleteApiKeysKeyId,
    postApiKeys,
} from "api/generated/api-keys/api-keys";
import type { UserProfile } from "api/generated/model";
import {
    ApiKeyWithSecretDto,
    InsertableApiKey,
    ApiKeyListDto,
    ApiKeyDtoContent,
    ApiScopeData,
    ApiScopeRealm,
    ApiScope,
} from "api/generated/model";
import { getUsersUserIdApiKeys } from "api/generated/users/users";
import { getApiScopes } from "api/generated/utils/utils";
import { DatePicker } from "components/datePicker";
import {
    BaseFormDisplay,
    BaseFormItem,
} from "components/form/legacyFields/formDisplay";
import { StringInput } from "components/form/legacyFields/stringInput";
import { CONSTANTS } from "utils/constants";
import {
    FormCreate,
    FormRootComponentProps,
    hasError,
    mapForm,
    ObjectError,
    simpleMappers,
} from "utils/form";

import { useFeatureFlag } from "../../utils/hooks/useFeatureFlag";

import * as styles from "./apiKeys.css";
import * as theme from "./theme.css";

export interface ApiKeysProps {
    ajaxManager: AjaxManager;
    orgProfileCookie: UserProfile | null;
}

type ApiKeyStatus =
    | { type: "DEFAULT" }
    | { type: "CREATE_KEY" }
    | { type: "SUBMITTING" }
    | { type: "DISPLAY_KEY"; key: string }
    | { type: "ERROR"; code: number | undefined };

type ApiScopes = Record<ApiScope, ApiScopeData>;

type ApiScopeParentCounter = Partial<
    Record<ApiScopeRealm, { count: number; checkedCount: number }>
>;

export interface ApiKeysState {
    status: ApiKeyStatus;
    counter: number;
}

type Dto = ApiKeyListDto;

export class ApiKeys extends React.PureComponent<ApiKeysProps, ApiKeysState> {
    private asyncComponent =
        React.createRef<AsyncComponent2<ApiKeyListDto, ApiScopes>>();

    state: ApiKeysState = {
        status: { type: "DEFAULT" },
        counter: 0,
    };

    private memoizeColumnBuilders: (
        dto: Dto
    ) => Record<string, ColumnBuilder<ApiKeyDtoContent, any>> = memoizeOne(
        (_dto) => {
            return {
                delete: (
                    _extractor: DataExtractor<ApiKeyDtoContent, string>
                ) => ({
                    getFormattedValue: (row: ApiKeyDtoContent) => {
                        return (
                            <Popconfirm
                                title={`${t("ApiKey_AskDeleteKey")}`}
                                okText={t("Classification_Mapping_InUse_Yes")}
                                cancelText={t(
                                    "Classification_Mapping_InUse_No"
                                )}
                                onConfirm={() => {
                                    this.deleteEntry(row.resource.id);
                                }}
                                placement={"topRight"}
                            >
                                <Button
                                    id={`delete-${row.resource.id}`}
                                    title={t("ApiKey_DeleteKey")}
                                    size="icon"
                                    className={styles.deleteKeyButton}
                                    icon={<CloseOutlined />}
                                />
                            </Popconfirm>
                        );
                    },
                    display: "DEFAULT_VISIBLE",
                    sortable: false,
                    layout: {
                        fixedWidth: 30,
                    },
                }),
            };
        }
    );

    private memoizeDataView: (dto: Dto) => DataView<ApiKeyDtoContent> =
        memoizeOne((dto) => {
            const stringCompare = Cmp.string();
            const standardExtractors: Array<
                DataExtractor<ApiKeyDtoContent, unknown>
            > = [
                {
                    id: "name",
                    label: t("ApiKey_List_Column_Name"),
                    getValue: (key: ApiKeyDtoContent) => key.resource.name,
                    compare: Cmp.map((key) => key.resource.name, stringCompare),
                    asString: (key) => key.resource.name,
                },
                {
                    id: "createdAt",
                    label: t("ApiKey_List_Column_CreatedAt"),
                    getValue: (key: ApiKeyDtoContent) => key.resource.createdAt,
                    compare: Cmp.map(
                        (key) => key.resource.createdAt,
                        stringCompare
                    ),
                    asString: (key) =>
                        formatDateTimeFast(parseDate(key.resource.createdAt)),
                },
                {
                    id: "lastUsedAt",
                    label: t("ApiKey_List_Column_LastUsedAt"),
                    getValue: (key: ApiKeyDtoContent) =>
                        key.resource.lastUsedAt,
                    compare: Cmp.map(
                        (key) => key.resource.lastUsedAt,
                        Cmp.undefinedLast(stringCompare)
                    ),
                    asString: (key) =>
                        key.resource.lastUsedAt !== null &&
                        key.resource.lastUsedAt !== undefined
                            ? formatDateTimeFast(
                                  parseDate(key.resource.lastUsedAt)
                              )
                            : t("ApiKey_List_NeverUsed"),
                },
                {
                    id: "expiresAt",
                    label: t("ApiKey_List_Column_ExpiresAt"),
                    getValue: (key: ApiKeyDtoContent) => key.resource.expiresAt,
                    compare: Cmp.map(
                        (key) => key.resource.expiresAt,
                        Cmp.undefinedLast(stringCompare)
                    ),
                    asString: (key) =>
                        key.resource.expiresAt !== null &&
                        key.resource.expiresAt !== undefined
                            ? formatDateTimeFast(
                                  parseDate(key.resource.expiresAt)
                              )
                            : t("ApiKey_List_NeverExpires"),
                },
                {
                    id: "scopes",
                    label: t("ApiKey_List_Column_Scopes"),
                    getValue: (key: ApiKeyDtoContent) => key.resource.scopes,
                    compare: Cmp.map(
                        (key) =>
                            key.resource.scopes.map(scopeToLabel).join(", "),
                        stringCompare
                    ),
                    asString: (key) =>
                        key.resource.scopes.map(scopeToLabel).join(", "),
                },
                {
                    id: "delete",
                    label: "",
                    getValue: () => "",
                    compare: Cmp.map(
                        (key) => key.resource.expiresAt,
                        Cmp.undefinedLast(stringCompare)
                    ),
                    asString: (key) =>
                        key.resource.expiresAt !== null &&
                        key.resource.expiresAt !== undefined
                            ? formatDateTimeFast(
                                  parseDate(key.resource.expiresAt)
                              )
                            : t("ApiKey_List_NeverExpires"),
                },
            ];

            const dataView: DataView<ApiKeyDtoContent> = {
                dataSource: new ArrayDataSource(dto.content, (user) =>
                    user.resource.id.toString()
                ),
                extractorGroups: [
                    {
                        id: "standard",
                        label: "Standard",
                        extractors: standardExtractors,
                    },
                ],
                sorters: [],
                defaultCompareItems: Cmp.map(
                    (user) => user.resource.id,
                    Cmp.primitive
                ),
            };
            return dataView;
        });

    private async deleteEntry(id: number) {
        await deleteApiKeysKeyId(id);
        if (this.asyncComponent.current) {
            this.asyncComponent.current.reload();
        }
    }

    render = () => {
        const { ajaxManager, orgProfileCookie } = this.props;
        const { counter } = this.state;

        if (orgProfileCookie === null) {
            // We are not logged-in anymore
            return undefined;
        }

        const { userId } = orgProfileCookie;

        return (
            <AsyncComponent2<ApiKeyListDto, ApiScopes>
                ref={this.asyncComponent}
                ajaxManager={ajaxManager}
                key={`api-keys-${counter}`}
                promises={[
                    () => getUsersUserIdApiKeys(userId),
                    () =>
                        getApiScopes().then((response) => ({
                            ...response,
                            data: response.data as ApiScopes,
                        })),
                ]}
                renderContent={this.renderContent}
                renderError={(code) => <LoggedInHTTPError code={code} />}
                renderLoading={() => <PageSpinner />}
            />
        );
    };

    private renderContent = (dto: ApiKeyListDto, scopes: ApiScopes) => {
        const dataView = this.memoizeDataView(dto);
        const columnBuilders = this.memoizeColumnBuilders(dto);
        return (
            <>
                {this.renderPopup(scopes)}
                <DataTable
                    id={"api-keys"}
                    dataView={dataView}
                    columnBuilders={columnBuilders}
                    footerRenderer={this.renderFooter}
                />
            </>
        );
    };

    private renderFooter = () => {
        return (
            <tr className="ant-table-row ant-table-row-level-0">
                <td colSpan={6}>
                    <Button
                        disabled={this.state.status.type === "SUBMITTING"}
                        id="create-api-key"
                        variant="link"
                        onClick={() =>
                            this.setState({ status: { type: "CREATE_KEY" } })
                        }
                    >
                        {t("ApiKey_CreateKey")}
                    </Button>
                </td>
            </tr>
        );
    };

    private renderPopup = (scopes: ApiScopes) => {
        const { status } = this.state;
        switch (status.type) {
            case "DEFAULT":
                return undefined;
            case "CREATE_KEY":
            case "SUBMITTING":
                return (
                    <ApiKeyForm
                        onCancel={this.cancelCreateApiKey}
                        onSubmit={this.createApiKey}
                        scopes={scopes}
                    />
                );
            case "DISPLAY_KEY":
                return (
                    <InformationModal
                        open
                        onClose={this.closeDisplayKey}
                        type="success"
                        title={t("ApiKey_Display_Title")}
                        // Wider than a plain message: the key has to be readable.
                        width={520}
                        footer={
                            <Button
                                variant="primary"
                                onClick={this.closeDisplayKey}
                            >
                                {t("Close")}
                            </Button>
                        }
                    >
                        <>
                            {t("ApiKey_Display_Description")}
                            <Input
                                type="text"
                                readOnly
                                className={theme.displayKey}
                                onClick={(event) => {
                                    event.currentTarget.setSelectionRange(
                                        0,
                                        status.key.length
                                    );
                                    navigator.clipboard
                                        .writeText(status.key)
                                        .then(() => {
                                            notification.success({
                                                message: t(
                                                    "ApiKey_Display_CopiedToClipboard"
                                                ),
                                            });
                                        });
                                }}
                                value={status.key}
                            />
                        </>
                    </InformationModal>
                );
            case "ERROR":
                console.error(`ERROR with code ${status.code}`);
                return undefined;
            default:
                assertNever(status);
        }
    };

    private createApiKey = (payload: InsertableApiKey) => {
        const { ajaxManager } = this.props;
        ajaxManager.ajax<ApiKeyWithSecretDto, ApiKeyStatus>(
            () => postApiKeys(payload),
            {
                component: this,
                initialStatusValue: "CREATE_KEY",
                inFlightStatus: { type: "SUBMITTING" },
                getSuccessStatus: ([response]) => {
                    this.setState({ counter: this.state.counter + 1 });
                    return {
                        type: "DISPLAY_KEY",
                        key: response.content.resource.key,
                    };
                },
                getErrorStatus: (code) => {
                    return {
                        type: "ERROR",
                        code,
                    };
                },
            }
        );
    };

    private cancelCreateApiKey = () => {
        if (this.state.status.type === "CREATE_KEY") {
            this.setState({ status: { type: "DEFAULT" } });
        }
    };

    private closeDisplayKey = () =>
        this.setState({ status: { type: "DEFAULT" } });
}

interface ApiKeyProps extends FormRootComponentProps<InsertableApiKey> {
    onCancel: () => void;
    onSubmit: (payload: InsertableApiKey) => void;
    scopes: ApiScopes;
}

interface ApiKeyState {
    spinning: boolean;
    checked: ApiScope[];
    scopeError: boolean;
}

function ApiKeyInternalFeatureFlagFiltered(props: ApiKeyProps) {
    const scimEnabled = useFeatureFlag("scim");
    const publicApiAttachmentsEnabled = useFeatureFlag("publicApiAttachments");
    const { scopes, ...otherProps } = props;
    const isRealmEnabled = (realm: ApiScopeRealm): boolean => {
        switch (realm) {
            case "Users":
                return scimEnabled;
            case "Attachments":
                return publicApiAttachmentsEnabled;
            case "ControlResults":
            case "DataSheets":
            case "Controls":
            case "Risks":
                return true;
        }
    };
    const filteredScopes = fromEntries(
        entries(scopes).filter(([_scope, data]) => isRealmEnabled(data.parent))
    );

    return <ApiKeyInternal scopes={filteredScopes} {...otherProps} />;
}

class ApiKeyInternal extends React.Component<ApiKeyProps, ApiKeyState> {
    state: ApiKeyState = {
        spinning: false,
        checked: [],
        scopeError: false,
    };

    render = () => {
        const { form, onCancel, scopes } = this.props;
        const { spinning, checked, scopeError } = this.state;
        const expiresAtMappers = simpleMappers<
            "expiresAt",
            string | undefined,
            Moment | null,
            InsertableApiKey
        >(
            "expiresAt",
            (expiresAtString) =>
                expiresAtString !== undefined ? moment(expiresAtString) : null,
            (expiresAtMoment) =>
                expiresAtMoment !== null
                    ? expiresAtMoment.toISOString()
                    : undefined
        );
        const expiresAtForm = mapForm<{ expiresAt: Moment | null }>()(
            form,
            expiresAtMappers()
        );

        const impliedScopes = this.getImpliedScopes(checked, scopes);
        const scopesError = scopeError
            ? t("ApiKey_Form_Scopes_Missing")
            : undefined;

        return (
            <Modal
                variant="content"
                open
                onClose={onCancel}
                title={t("ApiKey_Form_Title")}
                footer={
                    <>
                        <Button onClick={onCancel}>{t("Cancel")}</Button>
                        <Button variant="primary" onClick={this.onSubmit}>
                            {t("Create")}
                        </Button>
                    </>
                }
            >
                <Spin spinning={spinning}>
                    <StringInput
                        id="name"
                        type="CREATE"
                        label={t("ApiKey_Form_Name")}
                        form={form}
                        validators={[
                            {
                                kind: "ruleset",
                                required: true,
                                displayField: t("ApiKey_Form_Name"),
                                whitespace: true,
                                max: CONSTANTS.USER_CONSTANTS
                                    .MAX_API_KEY_NAME_LEN,
                            },
                        ]}
                        initialValue={undefined}
                    />
                    <BaseFormDisplay label={t("ApiKey_Form_ExpiresAt")}>
                        {expiresAtForm.getFieldDecorator(
                            "expiresAt",
                            {}
                        )(
                            <DatePicker
                                name={"expiresAt"}
                                id={"expiresAt"}
                                disabledDate={(date) =>
                                    date === null || date < moment()
                                }
                                placeholder={t("ApiKey_Form_NeverExpires")}
                            />
                        )}
                    </BaseFormDisplay>
                    <br />
                    <BaseFormItem
                        className={theme.noLabelBottomMargin}
                        error={scopesError}
                    >
                        <BaseFormDisplay label={t("ApiKey_Form_Scopes")}>
                            <Tree
                                selectable={false}
                                multiple={false}
                                checkable
                                checkedKeys={{
                                    checked: [
                                        ...checked,
                                        ...impliedScopes,
                                        ...this.getCheckedParents(
                                            checked,
                                            impliedScopes,
                                            scopes
                                        ),
                                    ],
                                    halfChecked: this.getHalfChecked(
                                        checked,
                                        impliedScopes,
                                        scopes
                                    ),
                                }}
                                onCheck={this.onCheckEntry}
                                treeData={this.treeDataBuilder(
                                    checked,
                                    impliedScopes,
                                    scopes
                                )}
                                checkStrictly={true}
                                showIcon={true}
                                icon={(nodeData: AntdTreeNodeAttribute) => {
                                    return (
                                        <InfoTooltip
                                            size="small"
                                            text={scopeOrRealmToHelpText(
                                                nodeData.eventKey as ApiScope
                                            )}
                                        />
                                    );
                                }}
                            />
                        </BaseFormDisplay>
                    </BaseFormItem>
                </Spin>
            </Modal>
        );
    };

    private isApiScope(scope: ApiScope | ApiScopeRealm): scope is ApiScope {
        const { scopes } = this.props;
        return Object.keys(scopes).includes(scope);
    }

    private getChildScopes = (realm: ApiScopeRealm): ApiScope[] => {
        return entries(this.props.scopes)
            .filter(([_k, v]) => v.parent === realm)
            .map(([k, _v]) => k);
    };

    private onCheckEntry: TreeProps["onCheck"] = (_checkedKeys, e) => {
        this.setState((prevState) => {
            const { checked } = prevState;
            const key = e.node.key as ApiScope | ApiScopeRealm;
            let checkedKeys = [...checked];

            if (this.isApiScope(key)) {
                if (e.checked) {
                    checkedKeys.push(key);
                } else {
                    checkedKeys = checked.filter((scope) => scope !== key);
                }
            } else {
                const childScopes = this.getChildScopes(key);
                if (e.checked) {
                    checkedKeys.push(...childScopes);
                } else {
                    checkedKeys = checked.filter(
                        (scope) => !childScopes.includes(scope)
                    );
                }
            }

            return {
                ...prevState,
                checked: checkedKeys.filter(
                    (value, index) => checkedKeys.indexOf(value) === index
                ),
            };
        });
    };

    private getImpliedScopes = memoizeOne(
        (checked: ApiScope[], scopes: ApiScopes): ApiScope[] => {
            const impliedScopes: ApiScope[] = [];

            for (const [key, value] of entries(scopes)) {
                if (checked.includes(key)) {
                    impliedScopes.push(...value.implied);
                }
            }
            return impliedScopes.filter(
                (value, index) => impliedScopes.indexOf(value) === index
            );
        }
    );

    private getHalfChecked = memoizeOne(
        (
            checked: ApiScope[],
            impliedScopes: ApiScope[],
            scopes: ApiScopes
        ): ApiScopeRealm[] => {
            const checkedData: ApiScopeParentCounter = {};

            for (const [key, value] of entries(scopes)) {
                const keyTyped = key;
                if (!checkedData[value.parent]) {
                    checkedData[value.parent] = { checkedCount: 0, count: 0 };
                }
                const parent = checkedData[value.parent];
                if (parent) {
                    parent.count++;
                    if (
                        checked.includes(keyTyped) ||
                        impliedScopes.includes(keyTyped)
                    ) {
                        parent.checkedCount++;
                    }
                }
            }
            return entries(checkedData)
                .filter(
                    (data) =>
                        data[1].checkedCount !== 0 &&
                        data[1].count > data[1].checkedCount
                )
                .map((data) => data[0]);
        }
    );

    private getCheckedParents = memoizeOne(
        (
            checked: ApiScope[],
            impliedScopes: ApiScope[],
            scopes: ApiScopes
        ): ApiScopeRealm[] => {
            const checkedData: ApiScopeParentCounter = {};

            for (const [key, value] of entries(scopes)) {
                const keyTyped = key;
                if (!checkedData[value.parent]) {
                    checkedData[value.parent] = { checkedCount: 0, count: 0 };
                }
                const parent = checkedData[value.parent];
                if (parent) {
                    parent.count++;
                    if (
                        checked.includes(keyTyped) ||
                        impliedScopes.includes(keyTyped)
                    ) {
                        parent.checkedCount++;
                    }
                }
            }
            return entries(checkedData)
                .filter(
                    (data) =>
                        data[1].checkedCount !== 0 &&
                        data[1].count === data[1].checkedCount
                )
                .map((data) => data[0]);
        }
    );

    private onSubmit = () => {
        const { form, scopes } = this.props;
        const { checked } = this.state;
        let checkedScopes: ApiScope[] = [
            ...checked,
            ...this.getImpliedScopes(checked, scopes),
        ];
        checkedScopes = checkedScopes.filter(
            (value, index) => checkedScopes.indexOf(value) === index
        );
        let hasScopeError = false;
        if (checkedScopes.length === 0) {
            hasScopeError = true;
            this.setState({
                scopeError: true,
            });
        }
        // We validate even if we have scope errors to show error messages for any other empty field
        form.validateFields({ force: true }, (errors: ObjectError, values) => {
            if (!hasError(errors) && !hasScopeError) {
                if (this.state.spinning) {
                    return;
                }
                this.setState({ spinning: true });

                values.scopes = checkedScopes;

                this.props.onSubmit(values);
            }
        });
    };

    private treeDataBuilder = memoizeOne(
        (
            _checked: ApiScope[],
            impliedScopes: ApiScope[],
            scopes: ApiScopes
        ): TreeNodeNormal[] => {
            const treeData: Map<ApiScopeRealm, TreeNodeNormal> = new Map();
            for (const [key, value] of entries(scopes)) {
                if (!treeData.get(value.parent)) {
                    treeData.set(value.parent, {
                        key: value.parent,
                        title: scopeRealmToLabel(value.parent),
                        children: [],
                    });
                }
                treeData.get(value.parent)?.children?.push({
                    key,
                    title: scopeToLabel(key),
                    disableCheckbox: impliedScopes.includes(key),
                });
            }
            const values = Array.from(treeData.values()).map((node) =>
                node.children?.length === 1 ? node.children[0] : node
            );
            return values;
        }
    );
}

const ApiKeyForm = FormCreate<InsertableApiKey, ApiKeyProps>()(
    ApiKeyInternalFeatureFlagFiltered
);

function scopeRealmToLabel(realm: ApiScopeRealm): string {
    switch (realm) {
        case "Attachments":
            return t("ApiKey_Form_Scopes_Attachments");
        case "ControlResults":
            return t("ApiKey_Form_Scopes_ControlResults");
        case "DataSheets":
            return t("ApiKey_Form_Scopes_DataSheets");
        case "Controls":
            return t("ApiKey_Form_Scopes_Controls");
        case "Risks":
            return t("ApiKey_Form_Scopes_Risks");
        case "Users":
            return t("ApiKey_Form_Scopes_Users");
        default:
            assertNever(realm);
    }
}

function scopeToLabel(scope: ApiScope): string {
    switch (scope) {
        case "Attachments_View":
            return t("ApiKey_Form_Scopes_Attachments_View");
        case "ControlResults_View":
            return t("ApiKey_Form_Scopes_ControlResults_View");
        case "DataSheets_UpdateContent":
            return t("ApiKey_Form_Scopes_DataSheets_UpdateContent");
        case "DataSheets_View":
            return t("ApiKey_Form_Scopes_DataSheets_View");
        case "Controls_Update":
            return t("ApiKey_Form_Scopes_Controls_Update");
        case "Controls_View":
            return t("ApiKey_Form_Scopes_Controls_View");
        case "Risks_View":
            return t("ApiKey_Form_Scopes_Risks_View");
        case "Users_Update":
            return t("ApiKey_Form_Scopes_Users_Update");
        case "Users_View":
            return t("ApiKey_Form_Scopes_Users_View");
        default:
            assertNever(scope);
    }
}

function scopeOrRealmToHelpText(scope: ApiScope | ApiScopeRealm) {
    switch (scope) {
        case "Attachments":
            return t("ApiKey_Form_Scopes_Attachments_HelpText");
        case "Attachments_View":
            return t("ApiKey_Form_Scopes_Attachments_View_HelpText");
        case "ControlResults":
            return t("ApiKey_Form_Scopes_ControlResults_HelpText");
        case "ControlResults_View":
            return t("ApiKey_Form_Scopes_ControlResults_View_HelpText");
        case "DataSheets":
            return t("ApiKey_Form_Scopes_DataSheets_HelpText");
        case "DataSheets_UpdateContent":
            return t("ApiKey_Form_Scopes_DataSheets_UpdateContent_HelpText");
        case "DataSheets_View":
            return t("ApiKey_Form_Scopes_DataSheets_View_HelpText");
        case "Controls_View":
            return t("ApiKey_Form_Scopes_Controls_View_HelpText");
        case "Controls_Update":
            return t("ApiKey_Form_Scopes_Controls_Update_HelpText");
        case "Controls":
            return t("ApiKey_Form_Scopes_Controls_HelpText");
        case "Risks_View":
            return t("ApiKey_Form_Scopes_Risks_View_HelpText");
        case "Risks":
            return t("ApiKey_Form_Scopes_Risks_HelpText");
        case "Users_Update":
            return t("ApiKey_Form_Scopes_Users_Update_HelpText");
        case "Users_View":
            return t("ApiKey_Form_Scopes_Users_View_HelpText");
        case "Users":
            return t("ApiKey_Form_Scopes_Users_HelpText");
        default:
            assertNever(scope);
    }
}
